import React from 'react';
import { QueryHookOptions } from '@apollo/client';
import { OperationData, OperationVariables, gql } from '@ts-gql/tag';
import { useQuery } from '@ts-gql/apollo';

const GET_CLIENTS_FOR_JOB = gql`
  query GetClientsForJob($cloudId: String!) {
    getJob(cloudId: $cloudId) {
      cloudId
      entities
    }
  }
` as import('../../__generated__/ts-gql/GetClientsForJob').type;

export type GetClientsForJobVariables = OperationVariables<
  typeof GET_CLIENTS_FOR_JOB
>;
export type GetClientsForJobResult = OperationData<typeof GET_CLIENTS_FOR_JOB>;

export type GetClientsForJobItem = NonNullable<
  ReturnType<typeof processData>
>[number];

const GET_ENTITIES_BY_IDS_FOR_JOB = gql`
  query GetEntitiesByIdsForJob($cloudIds: [String!]!) {
    getEntitiesByIds(cloudIds: $cloudIds) {
      cloudId
      name
      endDateUtc
      isDeleted
    }
  }
` as import('../../__generated__/ts-gql/GetEntitiesByIdsForJob').type;

export type GetEntitiesByIdsForJobVariables = OperationVariables<
  typeof GET_ENTITIES_BY_IDS_FOR_JOB
>;

export type GetEntitiesByIdsForJobResult = OperationData<
  typeof GET_ENTITIES_BY_IDS_FOR_JOB
>;

export const useGetClientsForJob = (
  options: QueryHookOptions<GetClientsForJobResult, GetClientsForJobVariables>
) => {
  const variables = options.variables as GetClientsForJobVariables;
  const jobRes = useQuery(GET_CLIENTS_FOR_JOB, {
    context: { service: 'core' },
    variables,
    ...options,
  });

  const entityArgs: GetEntitiesByIdsForJobVariables = React.useMemo(() => {
    return {
      cloudIds:
        jobRes &&
        jobRes.data &&
        jobRes.data.getJob &&
        jobRes.data.getJob.entities
          ? jobRes.data.getJob.entities
          : [],
    };
  }, [jobRes]);

  const entitiesRes = useQuery(GET_ENTITIES_BY_IDS_FOR_JOB, {
    context: { service: 'clients' },
    variables: entityArgs,
    skip: entityArgs.cloudIds.length === 0,
  });

  return React.useMemo(() => {
    if (jobRes.loading || entitiesRes.loading) {
      return { loading: true };
    }

    if (jobRes.error || entitiesRes.error || !entitiesRes.data) {
      return { error: true };
    }

    return { data: processData(entitiesRes.data) };
  }, [entitiesRes, jobRes]);
};

export function processData(data: GetEntitiesByIdsForJobResult | undefined) {
  const rawRows = data?.getEntitiesByIds;
  const result = rawRows?.map((raw) => ({
    ...raw,
    endDateUtc: raw.endDateUtc && new Date(raw.endDateUtc),
  }));
  return result;
}
